import * as R from 'remeda';
import { createSlice } from '../../core';
import type { CameraSlice } from '../../core/camera';
import {
  centerX,
  chooseZoomOrigin,
  constrainToViewport,
  fitX,
  getCanvasContext,
  translateToPosition,
  zoomAt,
} from '../../core/camera';
import type { Point2D } from '../../core/utils';

export type ZoomSlice = {
  zoom: {
    speed: number;
    minZoom: number;
    maxZoom: number;
    actions: {
      zoomBy: (delta: number, origin?: Point2D) => void;
      resetZoom: () => void;
      fitToWidth: () => void;
      setConfig: (
        config: Partial<{ speed: number; minZoom: number; maxZoom: number }>
      ) => void;
    };
  };
};

export const zoomSlice = createSlice<{
  canvas: CameraSlice['canvas'];
}>()<ZoomSlice>((set, get) => ({
  zoom: {
    speed: 0.001,
    minZoom: 0.1,
    maxZoom: 10,
    actions: {
      setConfig: config =>
        set(state => ({
          ...state,
          zoom: { ...state.zoom, ...config },
        })),

      zoomBy: (delta, origin) =>
        set(state => {
          const ctx = getCanvasContext(state.canvas);
          const { minZoom, maxZoom } = get().zoom;

          const zoomOrigin = chooseZoomOrigin(
            ctx,
            state.canvas.transform.zoom,
            origin
          );

          return {
            ...state,
            canvas: {
              ...state.canvas,
              transform: R.pipe(
                state.canvas.transform,
                zoomAt(ctx, delta, zoomOrigin, {
                  min: minZoom,
                  max: maxZoom,
                }),
                constrainToViewport(ctx)
              ),
            },
          };
        }),

      resetZoom: () =>
        set(state => {
          const ctx = getCanvasContext(state.canvas);
          return {
            ...state,
            canvas: {
              ...state.canvas,
              transform: R.pipe(
                { ...state.canvas.transform, zoom: 1 },
                constrainToViewport(ctx)
              ),
            },
          };
        }),

      fitToWidth: () =>
        set(state => {
          const ctx = getCanvasContext(state.canvas);
          return {
            ...state,
            canvas: {
              ...state.canvas,
              transform: R.pipe(
                state.canvas.transform,
                fitX(ctx),
                centerX(ctx),
                translateToPosition({ y: 0 }),
                constrainToViewport(ctx)
              ),
            },
          };
        }),
    },
  },
}));
